import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Source } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import { useMapboxToken } from './MapboxTokenContext';
import { useKnownSwims } from './KnownSwimsContext';
import 'mapbox-gl/dist/mapbox-gl.css';

const lineLayer = {
  id: 'quick-view-route-line',
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#ffffff', 'line-width': 4 }
};

const curatedRouteLayer = {
  id: 'quick-view-curated-routes',
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#007bff', 'line-width': 4 }
};

const CURATED_ROUTE_ZOOM = 10;
const CURATED_ROUTE_RELOAD_DELAY_MS = 250;

const intersectsBounds = (routeBounds, bounds) => routeBounds &&
  routeBounds.maxLat >= bounds.minLat &&
  routeBounds.minLat <= bounds.maxLat &&
  routeBounds.maxLng >= bounds.minLng &&
  routeBounds.minLng <= bounds.maxLng;

const boundsFromMap = (map) => {
  const bounds = map.getBounds();
  return {
    minLat: bounds.getSouth(),
    maxLat: bounds.getNorth(),
    minLng: bounds.getWest(),
    maxLng: bounds.getEast()
  };
};

const initialViewForSwim = (swim) => {
  if (swim?.geoBoundingBox) {
    const { minLat, maxLat, minLng, maxLng } = swim.geoBoundingBox;
    return {
      bounds: new mapboxgl.LngLatBounds([minLng, minLat], [maxLng, maxLat]),
      fitBoundsOptions: { padding: 24, maxZoom: 14 }
    };
  }
  const latitude = swim?.startCoordinate?._latitude;
  const longitude = swim?.startCoordinate?._longitude;
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude, zoom: 12 }
    : { longitude: -98.5795, latitude: 39.8283, zoom: 2 };
};

const SwimRouteQuickView = ({ swim, activeRoute, loading, onCurateArea }) => {
  const mapboxToken = useMapboxToken();
  const initialViewState = useMemo(() => initialViewForSwim(swim), [swim]);
  const [curatedRoutes, setCuratedRoutes] = useState([]);
  const [loadingCuratedRoutes, setLoadingCuratedRoutes] = useState(Boolean(swim.geoBoundingBox));
  const [mapBounds, setMapBounds] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);
  const moveTimeoutRef = useRef(null);
  const { knownSwims, loading: loadingKnownSwims, ensureKnownSwims } = useKnownSwims();

  useEffect(() => {
    setMapBounds(null);
    setMapZoom(null);
    setLoadingCuratedRoutes(Boolean(swim.geoBoundingBox));
    return () => clearTimeout(moveTimeoutRef.current);
  }, [swim.id, swim.geoBoundingBox]);

  useEffect(() => {
    ensureKnownSwims();
  }, [ensureKnownSwims]);

  useEffect(() => {
    if (!mapBounds || mapZoom === null) {
      setCuratedRoutes([]);
      return;
    }
    if (mapZoom < CURATED_ROUTE_ZOOM) {
      setCuratedRoutes([]);
      setLoadingCuratedRoutes(false);
      return;
    }

    setCuratedRoutes(
      knownSwims.filter((route) => intersectsBounds(route.geoBoundingBox, mapBounds) && route.route?.length >= 2)
    );
    setLoadingCuratedRoutes(loadingKnownSwims);
  }, [knownSwims, loadingKnownSwims, mapBounds, mapZoom]);

  const handleMapLoad = (event) => {
    setMapBounds(boundsFromMap(event.target));
    setMapZoom(event.target.getZoom());
  };

  const handleMapMoveEnd = (event) => {
    const map = event.target;
    const zoom = map.getZoom();
    setMapZoom(zoom);
    clearTimeout(moveTimeoutRef.current);
    if (zoom < CURATED_ROUTE_ZOOM) {
      setCuratedRoutes([]);
      setLoadingCuratedRoutes(false);
      return;
    }

    setLoadingCuratedRoutes(true);
    moveTimeoutRef.current = setTimeout(() => {
      setMapBounds(boundsFromMap(map));
    }, CURATED_ROUTE_RELOAD_DELAY_MS);
  };

  const curatedRouteFeatures = useMemo(() => ({
    type: 'FeatureCollection',
    features: curatedRoutes
      .map((route) => ({
        type: 'Feature',
        properties: { id: route.id },
        geometry: {
          type: 'LineString',
          coordinates: route.route.map((point) => [point.longitude, point.latitude])
        }
      }))
  }), [curatedRoutes]);

  return (
    <div className="swim-route-quick-view" onClick={(event) => event.stopPropagation()}>
      {swim.geoBoundingBox && (
        <div className="swim-route-quick-view-actions">
          <button type="button" onClick={() => onCurateArea?.(swim, activeRoute)}>Curate</button>
        </div>
      )}
      {loadingCuratedRoutes && <div className="swim-route-quick-view-spinner" role="status" aria-label="Loading curated swims" />}
      {mapZoom !== null && mapZoom < CURATED_ROUTE_ZOOM && (
        <div className="swim-route-quick-view-zoom-warning">Zoom to {CURATED_ROUTE_ZOOM} to load curated swims</div>
      )}
      {loading && <div className="swim-route-quick-view-status">Loading route...</div>}
      {!loading && !activeRoute && <div className="swim-route-quick-view-status">Route unavailable.</div>}
      <Map
        key={swim.id}
        initialViewState={initialViewState}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={mapboxToken}
        style={{ width: '100%', height: '260px' }}
        onLoad={handleMapLoad}
        onMoveEnd={handleMapMoveEnd}
      >
        <Source id="quick-view-curated-routes" type="geojson" data={curatedRouteFeatures}>
          <Layer {...curatedRouteLayer} />
        </Source>
        {activeRoute && (
          <Source id="quick-view-route" type="geojson" data={activeRoute}>
            <Layer {...lineLayer} />
          </Source>
        )}
      </Map>
    </div>
  );
};

export default SwimRouteQuickView;
